import { reactive, computed, watch, onMounted } from 'vue';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';

const MAX_SCHEDULES = 365;

export function useScheduling() {
  useI18n();

  const state = reactive({
    startDate: null,
    endDate: null,
    fromTime: null,
    fromTimeSlots: [],
    recurrenceType: 'daily',
    selectedDays: [],
    dayNames: {
      0: 'Domingo',
      1: 'Segunda-feira',
      2: 'Terça-feira',
      3: 'Quarta-feira',
      4: 'Quinta-feira',
      5: 'Sexta-feira',
      6: 'Sábado',
    },
  });

  // Função para calcular quantos agendamentos serão criados
  function calculateScheduleCount() {
    if (!state.startDate || !state.endDate) return 0;

    const start = new Date(state.startDate);
    const end = new Date(state.endDate);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    switch (state.recurrenceType) {
      case 'daily':
        return daysDiff;
      case 'weekly':
        return Math.ceil(daysDiff / 7);
      case 'monthly': {
        const monthsDiff =
          (end.getFullYear() - start.getFullYear()) * 12 +
          (end.getMonth() - start.getMonth()) +
          1;
        return monthsDiff;
      }
      case 'custom': {
        if (state.selectedDays.length === 0) return 0;
        const weeksInRange = Math.ceil(daysDiff / 7);
        return weeksInRange * state.selectedDays.length;
      }
      default:
        return 0;
    }
  }

  // Computed para verificar se está dentro do limite
  const scheduleCount = computed(() => calculateScheduleCount());
  const isWithinLimit = computed(() => scheduleCount.value <= MAX_SCHEDULES);

  const rules = {
    startDate: {
      required: helpers.withMessage('Data de início é obrigatória', required),
    },
    endDate: {
      required: helpers.withMessage('Data de fim é obrigatória', required),
    },
    fromTime: {
      required: helpers.withMessage('Horário é obrigatório', required),
    },
    recurrenceType: {
      required: helpers.withMessage('Recorrência é obrigatória', required),
    },
    selectedDays: {
      requiredIfCustom: helpers.withMessage(
        'Selecione pelo menos um dia',
        (value, vm) =>
          vm.recurrenceType !== 'custom' ||
          (Array.isArray(value) && value.length > 0)
      ),
    },
  };

  const v$ = useVuelidate(rules, state);

  // Geração de datas com limite rígido
  function generateScheduledDates() {
    if (!state.startDate || !state.endDate || !state.fromTime) return [];
    if (!isWithinLimit.value) return []; // Não gerar se exceder limite

    const scheduledDates = [];
    const [startYear, startMonth, startDay] = state.startDate
      .split('-')
      .map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const [endYear, endMonth, endDay] = state.endDate.split('-').map(Number);
    const end = new Date(endYear, endMonth - 1, endDay);
    const [hour, minute] = state.fromTime.split(':').map(Number);

    let current = new Date(start);
    let count = 0;

    if (state.recurrenceType === 'monthly') {
      while (current <= end && count < MAX_SCHEDULES) {
        const scheduledDate = new Date(
          current.getFullYear(),
          current.getMonth(),
          startDay
        );
        scheduledDate.setHours(hour, minute, 0, 0);

        if (
          scheduledDate >= start &&
          scheduledDate <= end &&
          scheduledDate.getDate() === startDay
        ) {
          const formattedDate = `${scheduledDate.getFullYear()}-${String(scheduledDate.getMonth() + 1).padStart(2, '0')}-${String(scheduledDate.getDate()).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          scheduledDates.push(formattedDate);
          count += 1;
        }
        current.setMonth(current.getMonth() + 1);
      }
    } else {
      while (current <= end && count < MAX_SCHEDULES) {
        let shouldAdd = false;

        switch (state.recurrenceType) {
          case 'daily':
            shouldAdd = true;
            break;
          case 'weekly':
            shouldAdd = current.getDay() === start.getDay();
            break;
          case 'custom':
            shouldAdd = state.selectedDays.includes(current.getDay());
            break;
          default:
            break;
        }

        if (shouldAdd) {
          const scheduledDate = new Date(current);
          scheduledDate.setHours(hour, minute, 0, 0);
          const formattedDate = `${scheduledDate.getFullYear()}-${String(scheduledDate.getMonth() + 1).padStart(2, '0')}-${String(scheduledDate.getDate()).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          scheduledDates.push(formattedDate);
          count += 1;
        }
        current.setDate(current.getDate() + 1);
      }
    }

    return scheduledDates;
  }

  // Prévia das datas
  const scheduledDatesPreview = computed(() => {
    const dates = generateScheduledDates();
    return dates.map(date => {
      const d = new Date(date);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
    });
  });

  // Data atual
  const currentDate = computed(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Geração de slots de tempo
  function generateTimeSlots() {
    const times = [];
    // Até 23:55 — "24:00" não é horário (a hora 24 gerava 24:00–24:55).
    for (let hour = 4; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 5) {
        const formattedHour = String(hour).padStart(2, '0');
        const formattedMinute = String(minute).padStart(2, '0');
        times.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return times;
  }

  const filteredTimeSlots = computed(() => {
    const today = currentDate.value;
    const isStartDateToday = state.startDate === today;

    if (!isStartDateToday) {
      return state.fromTimeSlots;
    }

    const getCurrentRoundedTime = () => {
      // Arredonda PRA CIMA no múltiplo de 5; 10:58 vira 11:00 (antes o
      // minuto 60 virava "00" sem somar a hora e caía em 10:00, no passado).
      const now = new Date();
      const total = now.getHours() * 60 + Math.ceil(now.getMinutes() / 5) * 5;
      const hours = Math.floor(total / 60);
      const minutes = total % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    const currentTime = getCurrentRoundedTime();
    return state.fromTimeSlots.filter(slot => slot >= currentTime);
  });

  function updateTimeSlots() {
    state.fromTimeSlots = generateTimeSlots();
  }

  // Watchers
  watch(
    () => state.startDate,
    newStartDate => {
      if (newStartDate && (!state.endDate || state.endDate < newStartDate)) {
        state.endDate = newStartDate;
      }
      updateTimeSlots();
    }
  );

  onMounted(() => {
    state.fromTimeSlots = generateTimeSlots();
  });

  function resetState() {
    state.startDate = null;
    state.endDate = null;
    state.fromTime = null;
    state.recurrenceType = 'daily';
    state.selectedDays = [];
  }

  return {
    schedulingState: state,
    v$,
    generateScheduledDates,
    scheduledDatesPreview,
    currentDate,
    filteredTimeSlots,
    resetState,
    // Limite simples
    scheduleCount,
    isWithinLimit,
    MAX_SCHEDULES,
  };
}
